import Swiper from "swiper";
import { AsyncPipe } from "@angular/common";
import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { MatDialog } from "@angular/material/dialog";
import { Router, RouterLink } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { allLanguageCodes, defaultLanguage, Language } from "@interfaces/translation-interface";
import { IonicModule, ModalController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { map, startWith, takeUntil, } from "rxjs/operators";
import { CommonVariable, FB_WELCOME_POSTER, FB_WELCOME_VIDEO } from "src/app/app.constants";
import { InitialUserContextService } from "src/app/services/initial-user-context.service";
import { LoggingService } from "src/app/services/logging.service";
import { MarketingCampaignsService } from "src/app/services/marketing-campaigns.service";
import { UserManagementService } from "src/app/services/user-management.service";
import type { SwiperOptions } from "swiper/types";
import { SwiperComponent, SwiperModule } from "swiper/angular";
import { RedirectsToSignupComponent } from "../redirects-to-signup/redirects-to-signup.component";
import { SignupPage } from "../signup/signup.page";

@Component({
  selector: "app-welcome",
  templateUrl: "./welcome.page.html",
  styleUrls: ["./welcome.page.scss"],
  standalone: true,
  imports: [
    IonicModule,
    SwiperModule,
    AsyncPipe,
    TranslatePipe,
    RouterLink,
  ],
})
export class WelcomePage implements OnInit, OnDestroy {
  @ViewChild('bgVideo', { static: false }) bgVideo!: ElementRef<HTMLVideoElement>;
  email_res: any = "email@gmail.com";

  welcomeVideo = FB_WELCOME_VIDEO;
  welcomePoster = FB_WELCOME_POSTER;
  @ViewChild("welcomeSwiper", { static: false }) welcomeSwiper: SwiperComponent;
  private destroy$ = new Subject<void>();
  private intervalId: number | null = null;

  private ngUnsubscribe = new Subject<void>();

  welcomePageData$ = this.marketingCampaignsService?.getWelcomePageData().pipe(
    map((data) => {
      return data;
    }),
    startWith([
      {
        _title: "Welcome to <br /> Lotto Social",
        _description: "Join our syndicates and get more <br />chances to win jackpots!",
      },
    ])
  );
  swiperOpts: SwiperOptions = {
    speed: 400,
    initialSlide: 0,
    pagination: true,
    loop: true,
  };
  spinnerStarted: boolean;
  spinnerStartedForAcc: boolean;
  selectedLanguage: string;
  speed: number;
  @HostListener("window:resize")
  onResize() {
    if (!this.welcomeSwiper) return;
    this.welcomeSwiper?.swiperRef?.update();
  }

  userId: string;
  private readonly loggingService = inject(LoggingService);
  private readonly initialUserContextService = inject(InitialUserContextService);
  private readonly NAVIGATION_TIMEOUT_MS = 1500;

  constructor(
    private analytics: AngularFireAnalytics,
    private modalCtrl: ModalController,
    private authService: AuthService,
    private dialog: MatDialog,
    private router: Router,
    private window: Window,
    public translate: TranslateService,
    private ngZone: NgZone,
    private marketingCampaignsService: MarketingCampaignsService,
    private userManagementService: UserManagementService,
  ) {
    this.speed = 500;
    this.authService?.selectedlanguage$?.pipe(takeUntil(this.destroy$)).subscribe(
      (l) => {
        this.selectedLanguage = l ? l : defaultLanguage;
        if (l) {
          translate.use(l ? l : defaultLanguage);
        }
      },
      (err) => {
        return err;
      }
    );
  }
  async ngOnInit() {
    this.initialUserContextService.resolveInitialContext();
    void this.initialUserContextService.confirmFromIpInBackground();

    await this.authService
      ?.getUid("WelcomePage")
      ?.then((uid) => {
        this.userId = uid;
        this.analytics.logEvent("what's_on_screen", {
          userId: this.userId,
        });
      })
      .catch((error) => {
        return error;
      });
    if (this.authService?.getReferralData()) {
      this.dialog.open(RedirectsToSignupComponent, {
        panelClass: "joined-syndicate-dialog",
        backdropClass: "cashout-back-drop-color",
      });
    }

    this.window["glowCookies"]?.start(this.selectedLanguage, {
      policyLink: "https://lottosocial.com/privacy-policy-for-lottery-syndicate-members/",
      hideAfterClick: true,
    });
    const localStorageLanguage: Language = localStorage?.getItem("language_code") as Language;
    if (localStorageLanguage && allLanguageCodes?.includes(localStorageLanguage))
      this.onLanguageChange(localStorageLanguage);
  }

  ngAfterViewInit() {
    if (!this.bgVideo || !this.bgVideo.nativeElement) {
      return;
    }

    const video = this.bgVideo.nativeElement;
    video.muted = true;

    const tryPlay = () => {
      const playPromise = video.play();
      if (playPromise !== undefined && playPromise.then) {
        playPromise
          .catch(err => {
            video.muted = true;
            video.play().catch(() => { });
          });
      }
    };

    tryPlay();
  }

  onLanguageChange(language: Language) {
    language = allLanguageCodes.includes(language) ? language : defaultLanguage;
    const prevLocalLang = localStorage.getItem("language_code") || "";
    this.userManagementService.setUserLanguageLogInFb(prevLocalLang, language, "welcome")
    localStorage.setItem("language_code", language);
    this.authService.selectedlanguage$.next(language);
    this.translate.use(language);
    this.selectedLanguage = language;
  }

  setSwiperInstance(swiper: Swiper) {
    try {
      this.ngZone.runOutsideAngular(() => {
        this.intervalId = window.setInterval(() => {
          // Update the counter variable
          this.ngZone.run(() => {
            if (swiper && typeof swiper.slideNext === 'function') {
              const speed = this.speed ?? 500; // Ensure speed is never undefined
              swiper.slideNext(speed);
            }
          });
        }, 3000);
      });
    } catch (err) {
      return err;
    }
  }
  async createAccount() {
    const createAccounts = await this.modalCtrl?.create({
      component: SignupPage,
      cssClass: "modal-fullScreen",
      // enterAnimation: CustomAnimation.enterFromRight,
      //  leaveAnimation: CustomAnimation.exitThroughRight,
    });

    return await createAccounts.present();
  }

  //navigate to create Account
  startSpinnerForCreateAcc(): void {
    this.spinnerStartedForAcc = true;
    void this.navigatePublicRoute('/signup', () => {
      this.spinnerStartedForAcc = false;
    });
  }

  //navigate to Login page
  startSpinnerForSignIn(event?: Event): void {
    event?.preventDefault();
    this.spinnerStarted = true;
    void this.navigatePublicRoute('/login', () => {
      this.spinnerStarted = false;
    });
  }

  private async navigatePublicRoute(
    path: '/login' | '/signup',
    resetSpinner: () => void
  ): Promise<void> {
    try {
      const navigated = await Promise.race([
        this.router.navigateByUrl(path),
        new Promise<boolean>((resolve) => {
          window.setTimeout(() => resolve(false), this.NAVIGATION_TIMEOUT_MS);
        }),
      ]).catch(() => false);

      if (!navigated) {
        this.loggingService.logWarning(`Welcome navigation fallback used for ${path}`);
        this.window.location.assign(path);
      }
    } catch (error) {
      this.loggingService.logError(`Welcome navigation failed for ${path}`, error as Error);
      this.window.location.assign(path);
    } finally {
      resetSpinner();
    }
  }

  ngOnDestroy() {
    // Clean up video element before component destruction to prevent race conditions
    // with third-party fullscreen cleanup scripts
    if (this.bgVideo && this.bgVideo.nativeElement) {
      const video = this.bgVideo.nativeElement;
      
      try {
        // Defensive check: ensure video element still exists and is connected to DOM
        if (!video || !video.pause) {
          return;
        }

        // Pause the video
        video.pause();
        
        // Exit fullscreen if active (both standard and webkit-prefixed APIs)
        const doc = document as Document & {
          webkitFullscreenElement?: Element;
          webkitExitFullscreen?: () => Promise<void>;
        };
        
        // Also check document-level fullscreen
        if (doc.fullscreenElement === video || doc.webkitFullscreenElement === video) {
          if (doc.exitFullscreen) {
            doc.exitFullscreen();
          } else if (doc.webkitExitFullscreen) {
            doc.webkitExitFullscreen();
          }
        }

        const v = video as HTMLVideoElement & {
          webkitPresentationMode?: 'inline' | 'picture-in-picture' | 'fullscreen';
          webkitSetPresentationMode?: (mode: 'inline' | 'picture-in-picture' | 'fullscreen') => void;
          webkitDisplayingFullscreen?: boolean;
          webkitExitFullscreen?: () => void;
        };

        // Enhanced defensive checks for webkit properties to prevent race conditions
        // during iOS Safari cleanup when navigating to another page
        try {
          // Check if webkit properties exist and are not null before accessing
          if (v && typeof v.webkitPresentationMode !== 'undefined' && v.webkitPresentationMode !== null) {
            if (v.webkitPresentationMode === 'fullscreen' && typeof v.webkitSetPresentationMode === 'function') {
              v.webkitSetPresentationMode('inline');
            }
          } else if (v && typeof v.webkitDisplayingFullscreen !== 'undefined' && v.webkitDisplayingFullscreen !== null) {
            if (v.webkitDisplayingFullscreen && typeof v.webkitExitFullscreen === 'function') {
              v.webkitExitFullscreen();
            }
          }
        } catch (webkitError) {
          // Silently handle webkit-specific errors during cleanup
          // This prevents crashes from race conditions with third-party scripts
        }

        // Remove all child <source> elements - with defensive checks
        if (video && video.firstChild) {
          while (video.firstChild) {
            video.removeChild(video.firstChild);
          }
        }
        
        // Clear video source to stop any ongoing loads - with defensive checks
        if (video && video.removeAttribute && video.load) {
          video.removeAttribute('src');
          video.load();
        }
      } catch  {}

    }

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete(); // Ensure cleanup
  }
}
